import React from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import { StoreUtil } from 'doctivity-shared/utils';

import { axiosInstance } from 'utils/axiosUtil';
import { listClients } from 'store/actions/clientsActions';
import { LoginPage } from './LoginPage';
import Logo from 'assets/images/login_logo.svg';

const PARAM_KEYS = [
  'client_id',
  'redirect_uri',
  'code_challenge',
  'code_challenge_method',
  'state',
  'scope',
  'client_name',
];

const STORAGE_KEY = 'oauth_consent';

const styles = (theme) => ({
  // Minimal, popup-friendly shell — deliberately NOT the app's marketing
  // login chrome. Centered column on a neutral background.
  root: {
    minHeight: '100vh',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(4, 2),
  },
  logo: {
    height: 64,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
  card: {
    width: 460,
    maxWidth: '92vw',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  para: {
    marginTop: theme.spacing(1),
  },
  formControl: {
    marginTop: theme.spacing(3),
    minWidth: 220,
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(4),
  },
  error: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
});

// Read the OAuth params from the URL on first load and stash them, so they
// survive the login round-trip (login navigates back to /oauth/consent but may
// drop the query string). On later mounts, restore from sessionStorage.
function loadParams() {
  const search = new URLSearchParams(window.location.search);
  if (search.get('client_id')) {
    const params = {};
    PARAM_KEYS.forEach((k) => { params[k] = search.get(k) || ''; });
    try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params)); } catch (e) { /* ignore */ }
    return params;
  }
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) { /* ignore */ }
  return null;
}

class OAuthConsentPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      params: loadParams(),
      selectedClientId: props.selectedClient || '',
      submitting: false,
      error: null,
    };
  }

  componentDidMount() {
    if (this.isAuthenticated() && !StoreUtil.isLoaded(this.props.clients)) {
      this.props.dispatch(listClients());
    }
  }

  componentDidUpdate(prevProps) {
    // After login completes (auth flips), make sure the client list is loaded.
    if (this.isAuthenticated() && !this.wasAuthenticated(prevProps)
        && !StoreUtil.isLoaded(this.props.clients)) {
      this.props.dispatch(listClients());
    }
  }

  wasAuthenticated(props) {
    const { user } = props;
    return !!(user && user.id && user.meta && user.meta.authenticated);
  }

  isAuthenticated() {
    return this.wasAuthenticated(this.props);
  }

  clientList() {
    const { clients } = this.props;
    return StoreUtil.isLoaded(clients) ? (StoreUtil.getData(clients) || []) : [];
  }

  resolveClientId() {
    const list = this.clientList();
    const { selectedClientId } = this.state;
    if (selectedClientId && list.some((c) => c.id === parseInt(selectedClientId, 10))) {
      return parseInt(selectedClientId, 10);
    }
    const sel = this.props.selectedClient;
    if (sel && list.some((c) => c.id === parseInt(sel, 10))) return parseInt(sel, 10);
    return list.length > 0 ? list[0].id : null;
  }

  submit(decision) {
    const { params } = this.state;
    const doctivityClientId = this.resolveClientId();
    if (decision === 'approve' && !doctivityClientId) {
      this.setState({ error: 'Please select a client to authorize.' });
      return;
    }
    this.setState({ submitting: true, error: null });

    axiosInstance
      .post('/v2/oauth/authorize/approve', {
        client_id: params.client_id,
        redirect_uri: params.redirect_uri,
        code_challenge: params.code_challenge,
        code_challenge_method: params.code_challenge_method || 'S256',
        state: params.state,
        scope: params.scope,
        decision,
        doctivity_client_id: doctivityClientId,
      })
      .then((res) => {
        try { window.sessionStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
        window.location.href = res.data.redirect_url;
      })
      .catch((err) => {
        const msg = err.response && err.response.data && err.response.data.error_description
          ? err.response.data.error_description
          : 'Authorization failed. Please try again.';
        this.setState({ submitting: false, error: msg });
      });
  }

  renderInner() {
    const { classes, user } = this.props;
    const { params, selectedClientId, submitting, error } = this.state;

    // Not signed in — reuse the existing login (keeps SMS 2FA, SAML, recovery).
    // Params are already stashed, so this route re-renders as consent after login.
    if (!this.isAuthenticated()) {
      return <LoginPage />;
    }

    if (!params || !params.client_id) {
      return (
        <Card className={classes.card}>
          <CardHeader title='Authorization request' />
          <CardContent>
            <Typography className={classes.error}>
              Missing or invalid authorization request. Please start the connection again from your app.
            </Typography>
          </CardContent>
        </Card>
      );
    }

    const list = this.clientList();
    const appName = params.client_name || params.client_id;

    return (
      <Card className={classes.card}>
        <CardHeader title='Authorize access to Doctivity' />
        <CardContent className={classes.cardContent}>
          <Typography variant='body1'>
            <strong>{appName}</strong> is requesting access to your Doctivity data.
          </Typography>
          <Typography variant='body2' className={classes.para} color='textSecondary'>
            {`Signed in as ${user.first_name || ''} ${user.last_name || ''} (${user.email || ''}).`}
          </Typography>
          <Typography variant='body2' className={classes.para} color='textSecondary'>
            {`Scope: ${params.scope || 'mcp'}. It will be able to read and act on data for the selected client only.`}
          </Typography>

          <FormControl className={classes.formControl}>
            <InputLabel id='oauth-client-label'>Client to authorize</InputLabel>
            <Select
              labelId='oauth-client-label'
              label='Client to authorize'
              value={selectedClientId || (this.resolveClientId() || '')}
              onChange={(e) => this.setState({ selectedClientId: e.target.value })}
              disabled={submitting || list.length === 0}
            >
              {list.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {error && <Typography className={classes.error}>{error}</Typography>}

          <div className={classes.buttons}>
            <Button onClick={() => this.submit('deny')} disabled={submitting}>
              Deny
            </Button>
            <Button
              variant='contained'
              color='primary'
              onClick={() => this.submit('approve')}
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={22} /> : 'Approve'}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  render() {
    const { classes } = this.props;
    return (
      <div className={classes.root}>
        <img src={Logo} alt='Doctivity' className={classes.logo} />
        {this.renderInner()}
      </div>
    );
  }
}

OAuthConsentPage.propTypes = {
  classes: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  user: PropTypes.object,
  clients: PropTypes.object,
  selectedClient: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

function mapStateToProps(state) {
  return {
    user: state.user,
    clients: StoreUtil.get(state.clients, StoreUtil.COMMON_LIST),
    selectedClient: state.app.selectedClient,
  };
}

const styled = withStyles(styles)(OAuthConsentPage);
const connected = connect(mapStateToProps)(styled);
export { connected as OAuthConsentPage };
